import React, { useEffect, useState } from "react";
import { Grid, Tab, Tabs, Typography } from "@mui/material";
import {
  setsConfigAnalysisFe55Tabs,
  setsConfigAnalysisNi63Tabs,
} from "../../utils/constants";
import {
  MAutocomplete,
  MTextField,
  MDatePicker,
  TabPanel,
  ResponsiveFormContainer,
} from "@app/components";
import { useEquipmentMide, useUser } from "@services/servicesGenericsTables";
import { MODES_OPTIONS } from "@app/types/generics.types";
import ChartAbsorptionForm from "./chartAbsorption";
import { useFormContext } from "react-hook-form";
import { KeysTabsAnalysis } from "@app/types/samples.types";

interface AnalysisFormProps {
  mode: string;
  typeAnalysis?: KeysTabsAnalysis;
}

const AnalysisFe55Form = ({ mode, typeAnalysis }: AnalysisFormProps) => {
  const form = useFormContext();
  const results = form.watch("results");
  const { userList } = useUser();
  const [tabValue, setTabValue] = useState(0);
  const handleChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };
  const { equipmentMideListFiltered, loading } = useEquipmentMide();
  const isView = mode === MODES_OPTIONS.VIEW;

  useEffect(() => {
    if (results)
      form.setValue(
        "uncertainty_calibration_source_counting_time_without_extinguishing_agent",
        results.u2relFa ?? ""
      );
  }, [results]);

  const setTabsConfig =
    `${typeAnalysis}` === KeysTabsAnalysis.Fe55
      ? setsConfigAnalysisFe55Tabs
      : setsConfigAnalysisNi63Tabs;

  return (
    <>
      <Tabs
        key={JSON.stringify(setTabsConfig)}
        value={tabValue}
        onChange={handleChange}
        aria-label="Fields uncertainty tabs"
      >
        {setTabsConfig.map((set, index) => (
          <Tab key={index} label={set.label} />
        ))}
      </Tabs>
      <TabPanel value={tabValue} index={0} padding={1}>
        <ResponsiveFormContainer columns={4}>
          <MAutocomplete
            name="analyst"
            options={userList}
            label="Seleccione un analista"
            getOptionLabel={(option) => option.username || ""}
            validation={{ required: "El analista es requerido" }}
            disabled={isView}
          />
          <MDatePicker
            name="date"
            pickerType="date"
            label="Fecha calibración"
            validation={{ required: "La fecha es requerida" }}
            disabled={isView}
          />

          <MAutocomplete
            key={JSON.stringify(form.watch("equipment"))}
            name="equipment"
            options={equipmentMideListFiltered}
            label="Contador centelleo líquido"
            getOptionLabel={(option) =>
              `${option.codigo} - ${option.descripcion}` || ""
            }
            loading={loading}
            disabled={isView}
          />
        </ResponsiveFormContainer>

        <Grid item xs={12} my={2}>
          <Typography variant="tagText">{"Datos patrón primario"}</Typography>
        </Grid>
        <ResponsiveFormContainer columns={2}>
          <MTextField
            name="ta0"
            label="Actividad del patrón primario (Bq/mg)"
            placeholder="Actividad del patrón primario (Bq/mg)"
            type="number"
            validation={{ required: "La actividad es requerida" }}
            fullWidth
            disabled={isView}
          />
          <MTextField
            name="tua0"
            label="Incertidumbre patrón primario para k=2 (Bq/mg)"
            helperText="(*) Aplica los datos del certificado de la fuente patrón"
            placeholder="Incertidumbre patrón primario para k=2 (Bq/mg)"
            type="number"
            validation={{ required: "La incertidumbre es requerida" }}
            fullWidth
            disabled={isView}
          />
        </ResponsiveFormContainer>

        <Grid item xs={12} my={2}>
          <Typography variant="tagText">
            {"Datos preparación patrón secundario"}
          </Typography>
        </Grid>
        <ResponsiveFormContainer columns={2}>
          <MTextField
            name="tmpp"
            label="Masa patrón primario (mpp) (mg)"
            placeholder="Masa patrón primario (mpp) (mg)"
            type="number"
            validation={{ required: "La masa es requerida" }}
            fullWidth
            disabled={isView}
            step={0.01}
          />
          <MTextField
            name="tmps"
            label="Masa patrón secundario (mps) (mg)"
            placeholder="Masa patrón secundario (mps) (mg)"
            type="number"
            validation={{ required: "La masa es requerida" }}
            fullWidth
            disabled={isView}
            step={0.01}
          />
          <MTextField
            name="tertiary_patron_mass"
            label="Masa patrón terciario (mpt) (mg)"
            placeholder="Masa patrón terciario (mpt) (mg)"
            type="number"
            validation={{ required: "La masa es requerida" }}
            fullWidth
            disabled={isView}
            step={0.01}
          />
        </ResponsiveFormContainer>

        <Grid item xs={12} my={2}>
          <Typography variant="tagText">
            {"Datos preparación fuente de calibración"}
          </Typography>
        </Grid>
        <ResponsiveFormContainer columns={2}>
          <MTextField
            name="tmpsfcal"
            label="Masa del patrón en la fuente de calibración sin agente extintor (mg)"
            placeholder="Masa del patrón en la fuente de calibración sin agente extintor (mg)"
            type="number"
            validation={{ required: "La masa es requerida" }}
            fullWidth
            disabled={isView}
            step={0.01}
          />
          <MTextField
            name="tdpmg"
            label="Actividad del patrón en la fuente de calibración sin agente extintor (dpm)"
            placeholder="Actividad del patrón en la fuente de calibración sin agente extintor (dpm)"
            type="number"
            validation={{ required: "La actividad es requerida" }}
            fullWidth
            disabled={isView}
            step={0.001}
          />
          <MTextField
            name="tcpmps"
            label="cpm de la fuente de calibración sin agente extintor"
            placeholder="cpm de la fuente de calibración sin agente extintor"
            type="number"
            validation={{ required: "El cpm es requerido" }}
            fullWidth
            disabled={isView}
            step={0.01}
          />
          <MTextField
            name="tef"
            label="Eficiencia de la fuente de calibración sin agente extintor"
            placeholder="Eficiencia de la fuente de calibración sin agente extintor"
            type="number"
            validation={{ required: "La eficiencia es requerida" }}
            fullWidth
            disabled={isView}
            step={0.001}
          />
          <MTextField
            name="tcal"
            label="Tiempo de contaje de la fuente de calibración sin agente extintor (min)"
            placeholder="Tiempo de contaje de la fuente de calibración sin agente extintor (min)"
            type="number"
            validation={{ required: "El tiempo de contaje es requerido" }}
            fullWidth
            disabled={isView}
          />
          <MTextField
            name="uncertainty_calibration_source_counting_time_without_extinguishing_agent"
            label="Incertidumbre relativa al cuadrado del factor de autoabsorción (u2relFa)"
            placeholder="Incertidumbre relativa al cuadrado del factor de autoabsorción (u2relFa)"
            type="number"
            fullWidth
            disabled
          />
          <MTextField
            name="tsie_sqp_patron_without_agent_extintor"
            label="Valor TSIE/SQP del patrón en la f.c. sin agente extintor"
            placeholder="Valor TSIE/SQP del patrón en la f.c. sin agente extintor"
            type="number"
            validation={{ required: "El tiempo de contaje es requerido" }}
            fullWidth
            disabled={isView}
            step={0.01}
          />
        </ResponsiveFormContainer>

        <ResponsiveFormContainer columns={2} mt={2}>
          <MTextField
            name="internal_comments"
            label="Comentarios Internos"
            placeholder="Comentarios Internos"
            type="textArea"
            rows={4}
            multiline
            fullWidth
            disabled={isView}
            step={0.01}
          />
        </ResponsiveFormContainer>
      </TabPanel>
      <TabPanel value={tabValue} index={1} padding={1}>
        <ChartAbsorptionForm mode={mode} />
      </TabPanel>
    </>
  );
};

export default AnalysisFe55Form;
